import { useCallback, useEffect, useRef, useState } from 'react';
import { chatService } from '../services/chat';
import { ChatAttachmentPreview, ChatMessage } from '../types/chat';
import { useMessagesRealtime } from './useMessagesRealtime';

/** Messages for a single thread, kept live and auto-marked as read. */
export function useConversationThread(conversationId: string | null, currentUserId?: string) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [attachments, setAttachments] = useState<Record<string, ChatAttachmentPreview>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const resolveAttachments = useCallback(async (rows: ChatMessage[]) => {
    const refs = rows
      .filter(m => m.attachment_type && m.attachment_id)
      .map(m => ({ type: m.attachment_type!, id: m.attachment_id! }));
    if (refs.length === 0) return;
    try {
      const previews = await chatService.resolveAttachments(refs);
      if (!mountedRef.current) return;
      setAttachments(prev => ({ ...prev, ...previews }));
    } catch {
      // Attachment previews are best-effort.
    }
  }, []);

  const load = useCallback(async () => {
    if (!conversationId) {
      setMessages([]);
      return;
    }
    setLoading(true);
    try {
      const rows = await chatService.getMessages(conversationId);
      if (!mountedRef.current) return;
      setMessages(rows);
      setError(null);
      resolveAttachments(rows);
      chatService.markRead(conversationId).catch(() => undefined);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err.message : 'Could not load this conversation');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [conversationId, resolveAttachments]);

  useEffect(() => {
    setMessages([]);
    setError(null);
    load();
  }, [load]);

  // Batch read receipts so a fast back-and-forth doesn't fire an RPC per message.
  const markReadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (markReadTimerRef.current) clearTimeout(markReadTimerRef.current);
    };
  }, []);

  const scheduleMarkRead = useCallback((id: string) => {
    if (markReadTimerRef.current) return;
    markReadTimerRef.current = setTimeout(() => {
      markReadTimerRef.current = null;
      chatService.markRead(id).catch(() => undefined);
    }, 1500);
  }, []);

  useMessagesRealtime(
    !!conversationId,
    (incoming: ChatMessage) => {
      // One shared channel serves the whole app, so filter to this thread.
      if (!conversationId || incoming.conversation_id !== conversationId) return;

      setMessages(prev => {
        if (prev.some(m => m.id === incoming.id)) return prev;
        return [...prev, incoming];
      });
      if (incoming.attachment_type && incoming.attachment_id) {
        resolveAttachments([incoming]);
      }
      if (incoming.sender_id !== currentUserId) {
        scheduleMarkRead(conversationId);
      }
    },
    load
  );

  const appendLocal = useCallback((message: ChatMessage) => {
    setMessages(prev => (prev.some(m => m.id === message.id) ? prev : [...prev, message]));
    if (message.attachment_type && message.attachment_id) {
      resolveAttachments([message]);
    }
  }, [resolveAttachments]);

  return { messages, attachments, loading, error, reload: load, appendLocal };
}
